// Plan 4 Task 4 — /admin/categories list page.
//
// Lists JobTypeCategory rows; New/Edit open the CategoryEditor modal which
// authors name + presets (default destinations + pipeline template). Delete
// may surface a 409 `category_in_use` — the page catches that and shows an
// inline "in use" notice instead of a generic failure toast.
import { useState } from "react";
import { AdminListHeader } from "@/components/admin/AdminListHeader";
import { ApiError } from "@/lib/api-client";
import { showApiError } from "@/lib/error-toast";
import {
  useJobTypeCategories,
  type JobTypeCategory,
  type CategoryWriteBody,
} from "./useJobTypeCategories";
import { CategoryEditor } from "./CategoryEditor";
import styles from "./CategoryEditor.module.css";

type EditorState =
  | { mode: "create" }
  | { mode: "edit"; category: JobTypeCategory }
  | null;

function isCategoryInUse(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    typeof err.body === "object" &&
    err.body !== null &&
    (err.body as { error?: string }).error === "category_in_use"
  );
}

export function CategoryListPage() {
  const { categories, isLoading, error, refresh, create, update, remove } =
    useJobTypeCategories();
  const [editing, setEditing] = useState<EditorState>(null);
  const [inUseNotice, setInUseNotice] = useState<string | null>(null);

  async function handleSubmit(body: CategoryWriteBody) {
    try {
      if (editing?.mode === "create") {
        await create(body);
      } else if (editing?.mode === "edit") {
        await update(editing.category.id, body);
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      showApiError(e);
    }
  }

  async function handleDelete(c: JobTypeCategory) {
    if (!window.confirm(`Delete category '${c.name}'?`)) return;
    setInUseNotice(null);
    try {
      await remove(c.id);
      await refresh();
    } catch (e) {
      if (isCategoryInUse(e)) {
        setInUseNotice(
          `Category "${c.name}" is in use by one or more job types and cannot be deleted.`,
        );
        return;
      }
      showApiError(e);
    }
  }

  return (
    <div className={styles.listPage}>
      <AdminListHeader
        title="Categories"
        count={categories.length}
        primaryAction={{
          label: "+ New category",
          onClick: () => setEditing({ mode: "create" }),
        }}
      />

      {inUseNotice && (
        <div className="admin-error" role="alert" style={{ margin: "0 16px 8px" }}>
          {inUseNotice}
        </div>
      )}

      {isLoading ? (
        <p className={styles.help} style={{ padding: "0 16px" }}>
          Loading…
        </p>
      ) : error ? (
        <div className="admin-error" style={{ margin: "0 16px" }}>
          Error: {error}
        </div>
      ) : categories.length === 0 ? (
        <div className="tab-placeholder">
          <span className="tab-placeholder-text">
            No categories yet. Categories group job types and seed new jobs with
            default export destinations and a pipeline template.
          </span>
          <button
            type="button"
            className={styles.primary}
            style={{ marginTop: "var(--space-4)" }}
            onClick={() => setEditing({ mode: "create" })}
          >
            Create one
          </button>
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Name</th>
              <th style={{ textAlign: "left" }}>Pipeline preset</th>
              <th style={{ textAlign: "left" }}>Destinations</th>
              <th style={{ textAlign: "left" }}>Order</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => {
              const destCount = c.presets?.defaultDestinations?.length ?? 0;
              return (
                <tr
                  key={c.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => setEditing({ mode: "edit", category: c })}
                >
                  <td>{c.name}</td>
                  <td>{c.presets?.defaultPipelineTemplate ?? "—"}</td>
                  <td>{destCount}</td>
                  <td>{c.displayOrder}</td>
                  <td style={{ textAlign: "right" }}>
                    <div className={styles.actions} style={{ marginTop: 0 }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditing({ mode: "edit", category: c });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className={styles.removeBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDelete(c);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {editing && (
        <CategoryEditor
          mode={editing.mode}
          initial={editing.mode === "edit" ? editing.category : undefined}
          onClose={() => setEditing(null)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
